import Modal from 'bz-core-controls/modal';
import AuthStore from 'bz-stores/lib/auth-store';
import { ConfigObservable } from 'bz-stores/lib/config';
import type { IObservableArray } from 'mobx';
import { action, computed, observable, runInAction } from 'mobx';
import { MfaState } from 'shell/header/components/user-popover/components/mfa';
import type { AuthenticateResponse, UserRoleLocationWithDetails } from 'tm3-core/api.dtos';
import {
  ChangePasswordRequest,
  ResetMode,
  UserLocationsRequest,
  UserLocationsResponse,
  UserRequest,
  UserResetRequest,
  UserSaveRequest,
  UsersSearchResponse,
  UsersWithRolesRequest
} from 'tm3-core/api.dtos';
import { cache } from 'tm3-core/cache';
import FormStore from 'tm3-core/controls/forms/form-store';
import EConfigKeys from 'tm3-core/enums/config-enum';

export enum DialogModes {
  MAIN,
  ADD_LOCATION,
  BASE_ON_EXISTING
}

export enum EAction {
  none,
  create,
  edit
}

class UserDialogFormStore extends FormStore<UserSaveRequest> {
  @observable action: EAction = EAction.none;
  @observable error?: string = undefined;
  @observable userLocations: IObservableArray<UserLocationsResponse> = observable.array([]);
  @observable allUsersRoles: IObservableArray<UserRoleLocationWithDetails> = observable.array([]);
  @observable dialogMode = DialogModes.MAIN;
  @observable isOwnUser: boolean = false;
  @observable exceededLicenses: boolean = false;
  @observable autoSelectLocation: boolean = false;
  @observable avatarLocal: string = '';
  @observable isChangePassword: boolean = false;
  @observable msgError: string = '';

  clickRules = {
    firstName: { presence: { allowEmpty: false, message: 'User First Name is required' } },
    lastName: { presence: { allowEmpty: false, message: 'User Last Name is required' } },
    email: {
      email: { email: true, message: 'Email is invalid' },
      presence: () => {
        return { allowEmpty: false, message: 'Email address is required' };
      }
    }
  };
  @observable mfaMode: MfaState = MfaState.Initial;
  @observable showMfa: boolean = false;

  constructor(props: any) {
    super(props);
    this.validationRules = {
      fullName: {
        presence: () => {
          if (!this.autoSelectLocation)
            return { allowEmpty: false, message: 'Full Name is required' };
          return false;
        }
      },
      locationId: {
        presence: () => {
          if (this.get('locationId')) return false;

          return {
            allowEmpty: false,
            message: 'Location is required'
          };
        },
        format: () => {
          const selected = this.get('locationId');
          const { userLocations } = this;
          if (!userLocations.find((x) => x.locationId === selected))
            return {
              format: false,
              message: 'The selected location must be added to the user locations below'
            };
          return false;
        }
      },
      email: { email: true, presence: { allowEmpty: false, message: 'Email address is required' } }
    };

    if (!!process.env.isClick) {
      this.validationRules = Object.assign({}, this.clickRules);
    }

    if (this.enforceMandatoryMobile) {
      this.validationRules.mobileNumber = {
        presence: { allowEmpty: false, message: 'Mobile number is required' }
      };
    }
  }

  get enforceMandatoryMobile(): boolean {
    return ConfigObservable.isEnabled(EConfigKeys.HIDE_MFA_OPTIONS);
  }

  @computed
  get getCurrentUserId() {
    return AuthStore.user.id;
  }

  @computed
  get unAddedLocations() {
    const { userLocations } = this;
    const addedLocationIds = userLocations.map((l) => l.locationId);
    return cache.locations.CurrentLocations.filter((l) => !addedLocationIds.includes(l.id));
  }

  @action.bound
  setAvatar(base64: string) {
    return (this.avatarLocal = base64);
  }

  @action.bound
  displayChangePasswordField() {
    return (this.isChangePassword = !this.isChangePassword);
  }

  @action.bound
  async updatePassword(data: ChangePasswordRequest): Promise<AuthenticateResponse> {
    const req: ChangePasswordRequest = new ChangePasswordRequest(data);
    return await this.client.post(req);
  }

  @action.bound
  async initialise(id?: number) {
    this.isLoading = true;
    const userRoleReq = new UsersWithRolesRequest();
    const roles = await this.client.get(userRoleReq);

    if (!id) {
      runInAction(() => {
        this.action = EAction.create;
        this.isLoading = false;
        this.isOwnUser = false;
        if (this.autoSelectLocation) {
          const locations = cache.locations.CurrentLocations.map((location) => {
            return new UserLocationsResponse({
              locationId: location.id,
              roleId: 1,
              locationName: location.name
            });
          });
          this.userLocations.replace(locations);
        } else {
          this.userLocations.clear();
        }

        this.allUsersRoles.replace(roles);
      });

      return new UsersSearchResponse({ current: true });
    }

    this.checkOwnUser(id);
    const userReq = new UserRequest({ id: id });
    const userLocationReq = new UserLocationsRequest({ userId: id });

    const userPromise = this.client.get(userReq);
    const userLocationPromise = this.client.get(userLocationReq);
    const [userRes, userLocationRes] = await Promise.all([userPromise, userLocationPromise]);
    if (!userRes.result.current && cache.isUserLicensed) {
      this.exceededLicenses = cache.authStore.usedLicenses >= cache.licensedSeatCount;
    }

    runInAction(() => {
      this.action = EAction.edit;
      this.allUsersRoles.replace(roles);
      this.userLocations.replace(userLocationRes.results);
      this.isLoading = false;
    });

    return userRes.result;
  }

  @action.bound
  checkOwnUser(id: number) {
    const userId = AuthStore.user.id;
    this.isOwnUser = id == userId;
  }

  @action.bound
  async onSave(data: UserSaveRequest) {
    if (!this.autoSelectLocation) {
      if (this.userLocations.length === 0) {
        return;
      }
    } else {
      data.fullName = `${data.firstName} ${data.lastName}`;
    }
    const req = new UserSaveRequest(data);
    req.locations = this.userLocations;
    if (req.name == null) {
      req.name = req.email;
    }

    let res;
    try {
      res = await this.client.post(req);

      runInAction(() => {
        this.action = EAction.none;
      });
    } catch (e) {
      this.msgError = e.responseStatus?.message ?? e?.message ?? e;

      throw e;
    }

    return res;
  }

  @action.bound
  async onResetPassword() {
    try {
      await Modal.confirmation({
        title: 'Confirm Password Reset',
        description: (
          <div>
            <p>
              You are resetting this user's password, they will receive an email with instructions
              to follow.
            </p>
            <p>Are you sure you wish to continue?</p>
          </div>
        )
      });
      await this.resetAuth(ResetMode.Password);
    } catch {}
  }

  @action.bound
  async onResetMFA() {
    try {
      if (!this.isOwnUser) {
        await Modal.confirmation({
          title: 'Confirm Multi Factor Authentication Reset',
          description: (
            <div>
              <p>You are resetting this user's multi factor authentication details.</p>
              <p className="m-4 rounded-md border border-orange-300 bg-orange-200 p-4">
                Please ensure you have verified their identity, and that their email account hasn't
                been compromised. You can also update their mobile number so they can multi factor
                authenticate using SMS.
              </p>
              <p>Are you sure you wish to continue?</p>
            </div>
          )
        });
        await this.resetAuth(ResetMode.MFA);
      } else {
        this.showMfa = true;
        this.mfaMode = MfaState.Initial;

        return;
      }
    } catch {}
  }

  @action.bound
  async onSetupMFA() {
    this.showMfa = true;
    this.mfaMode = MfaState.Setup;
  }

  @action.bound
  async onResetPassKey() {
    try {
      if (!this.isOwnUser) {
        await Modal.confirmation({
          title: 'Confirm Multi Factor Authentication Reset',
          description: (
            <div>
              <p>
                You are allowing this user to register for a new Pass Key. They will receive an
                email with instructions to follow.
              </p>
              <p className="m-4 border border-orange-300 bg-orange-200 p-4">
                Please ensure you have verified their identity, and that their email account hasn't
                been compromised.
              </p>
              <p>Are you sure you wish to continue?</p>
            </div>
          )
        });
        await this.resetAuth(ResetMode.PassKey);
      } else {
        this.showMfa = true;
        this.mfaMode = MfaState.SetupPassKey;
      }
    } catch {}
  }

  @action.bound
  async resetAuth(mode: ResetMode) {
    return await this.client.post(new UserResetRequest({ id: this.get('id'), mode }));
  }
}

export default UserDialogFormStore;
